import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, gbp, formatApiError, openPrivateFile } from "@/lib/api";
import { StatusBadge } from "@/components/site/StatusBadge";
import { ProfileCard } from "@/components/site/ProfileCard";

const date = (s) => new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

const Row = ({ title, meta, status, testId, children }) => (
  <div data-testid={testId} className="bg-white border border-[#E4E1D9] p-6 flex flex-col sm:flex-row sm:items-start justify-between gap-4">
    <div>
      <p className="font-display text-xl">{title}</p>
      <p className="mt-1 font-plex text-xs text-[#847F77]">{meta}</p>
      {children}
    </div>
    <StatusBadge status={status} testId={`${testId}-status`} />
  </div>
);

const Group = ({ title, empty, cta, items, render }) => (
  <section className="mt-12">
    <div className="flex items-center justify-between">
      <h2 className="font-display text-2xl">{title}</h2>
      {cta}
    </div>
    <div className="mt-5 space-y-3">
      {items.length ? items.map(render) : <p className="text-sm text-[#847F77] border border-dashed border-[#C5BCAB] p-6">{empty}</p>}
    </div>
  </section>
);

export default function Portal() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/me/submissions").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const viewDoc = (id) => openPrivateFile(id).catch((e) => toast.error(formatApiError(e)));

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-10 py-16" data-testid="portal-page">
      <p className="eyebrow">Client portal</p>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight" data-testid="portal-welcome">Hello, {user.full_name.split(" ")[0]}</h1>
      <p className="mt-3 text-[#57534E]">Track everything you've shared with us. We'll contact you personally at each step.</p>
      <ProfileCard />
      {!data ? <p className="mt-12 eyebrow">Loading…</p> : (
        <>
          <Group title="Buyer profiles" items={data.buyer_applications} empty="You haven't registered as a buyer yet."
            cta={<Link to="/buy" data-testid="portal-new-buyer" className="text-sm text-[#9F7E44] hover:text-[#866835]">+ New buyer profile</Link>}
            render={(b) => (
              <Row key={b.id} testId={`portal-buyer-${b.id}`} status={b.status}
                title={`Budget up to ${gbp(b.budget_max)}`} meta={`Submitted ${date(b.created_at)} · ${b.chain_status}`}>
                <button onClick={() => viewDoc(b.proof_file_id)} data-testid={`portal-view-proof-${b.id}`}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#57534E] hover:text-[#9F7E44]">
                  <FileText className="h-4 w-4" /> {b.proof_filename}
                </button>
              </Row>
            )} />
          <Group title="Properties submitted for sale" items={data.seller_submissions} empty="You haven't submitted a property yet."
            cta={<Link to="/sell" data-testid="portal-new-seller" className="text-sm text-[#9F7E44] hover:text-[#866835]">+ Submit property</Link>}
            render={(s) => (
              <Row key={s.id} testId={`portal-seller-${s.id}`} status={s.status}
                title={`${s.property_address}, ${s.postcode}`} meta={`Submitted ${date(s.created_at)} · ${s.timeline}`} />
            )} />
          <Group title="Property enquiries" items={data.enquiries} empty="No enquiries yet."
            cta={<Link to="/properties" className="text-sm text-[#9F7E44] hover:text-[#866835]">Browse properties</Link>}
            render={(q) => (
              <Row key={q.id} testId={`portal-enquiry-${q.id}`} status={q.status} title={q.property_title} meta={`Sent ${date(q.created_at)}`} />
            )} />
        </>
      )}
    </div>
  );
}
